import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { rosetteCurves } from "./rosette";
import { ROMAN } from "../utils";

/**
 * Engraved seal — a guilloché rosette that gets denser with the level (I → V), like the
 * security patterns on banknotes and share certificates. The recurring motif of the module.
 * state: locked | active | ready | validated
 * numeral={false} (theme programs: containers are not numbered) leaves a purely decorative seal.
 */
const buildRosette = (level) =>
  rosetteCurves(level).map(
    (pts) =>
      `${pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join("")}Z`
  );
const STATE = {
  locked: {
    stroke: "var(--rule-strong)",
    ring: "var(--rule-strong)",
    num: "var(--ink-3)",
    fill: "var(--paper)",
    op: 0.9,
  },
  active: {
    stroke: "var(--brand)",
    ring: "var(--brand)",
    num: "var(--brand-ink)",
    fill: "var(--surface)",
    op: 0.55,
  },
  ready: {
    stroke: "var(--gold-deep)",
    ring: "var(--brand)",
    num: "var(--brand-ink)",
    fill: "var(--surface)",
    op: 0.6,
  },
  validated: {
    stroke: "var(--gold-deep)",
    ring: "var(--gold-deep)",
    num: "var(--gold-ink)",
    fill: "var(--gold-soft)",
    op: 0.75,
  },
};
const Seal = ({ level = 1, state = "active", size = 96, numeral = true, className = "" }) => {
  const { t } = useTranslation();
  const paths = useMemo(() => buildRosette(level), [level]);
  const c = STATE[state] || STATE.active;
  const id = `seal-${level}-${state}`;
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      role={numeral ? "img" : undefined}
      aria-hidden={numeral ? undefined : "true"}
      aria-label={
        numeral
          ? t("Niveau {{level}}", {
              level,
            })
          : undefined
      }
      style={{
        flex: "none",
      }}
    >
      <defs>
        <clipPath id={`${id}-clip`}>
          <circle
            cx="100"
            cy="100"
            r="86"
          />
        </clipPath>
      </defs>
      <circle
        cx="100"
        cy="100"
        r="96"
        fill={c.fill}
        stroke={c.ring}
        strokeWidth="1.5"
      />
      <circle
        cx="100"
        cy="100"
        r="90.5"
        fill="none"
        stroke={c.ring}
        strokeWidth="5"
        strokeDasharray="0.9 3.1"
        opacity="0.55"
      />
      <g
        clipPath={`url(#${id}-clip)`}
        fill="none"
        stroke={c.stroke}
        strokeWidth="0.55"
        opacity={c.op}
      >
        {paths.map((d, i) => (
          <path
            key={i}
            d={d}
          />
        ))}
      </g>
      <circle
        cx="100"
        cy="100"
        r={numeral ? 33 : 20}
        fill={c.fill}
        stroke={c.ring}
        strokeWidth="1.2"
      />
      {numeral && (
        <text
          x="100"
          y="100"
          textAnchor="middle"
          dominantBaseline="central"
          fill={c.num}
          style={{
            fontFamily: "var(--f-serif)",
            fontSize: level >= 4 ? 30 : 36,
            fontWeight: 700,
            letterSpacing: level >= 3 ? "-1px" : "0",
          }}
        >
          {ROMAN[level]}
        </text>
      )}
    </svg>
  );
};
export default Seal;
